import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Heading,
  Stack,
  Image,
  Card,
  CardBody,
  Icon,
  SimpleGrid,
  HStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import MainHeader from "./MainHeader";
import Footer from "./Footer";
import {
  FcBriefcase,
  FcClock,
  FcConferenceCall,
  FcCurrencyExchange,
  FcHome,
  FcTodoList,
} from "react-icons/fc";
import { useTransition, animated } from "@react-spring/web";
import Landing from "./Landing";
import { trackCTA, trackOutboundLink } from "../functions/analytics";

const slides = [
  { name: "Winnipeg", route: "/winnipeg", img: "/winnipeg.jpg" },
  {
    name: "Greater Toronto and Hamilton Area",
    route: "/toronto",
    img: "/toronto.jpg",
  },
  { name: "Metro Vancouver", route: "/vancouver", img: "/vancouver.jpg" },
];

const Information = () => {
  const navigate = useNavigate();

  const [index, setIndex] = useState(0);

  const transitions = useTransition(index, {
    key: index,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { mass: 50, tension: 50, friction: 50 },

    onRest: (_a, _b, item) => {
      if (index === item) {
        setIndex((state) => (state + 1) % slides.length);
      }
    },
  });

  return (
    <Flex direction="column" minH="100vh">
      <MainHeader variant="info" />
      <Flex as="main" role="main" direction="column" flex="1">
        <Landing />
        <Stack
          bg="#ebe8f1"
          fontFamily="Garamond"
          marginTop="8px"
          height="calc(100vh - 100px)"
          onClick={() => {
            trackCTA("city_carousel");
            navigate("/cities");
          }}
        >
          {transitions((style, key) => (
            <animated.div
              key={key}
              style={{
                ...style,
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 100px)",
                width: "100%",
                position: "absolute",
                willChange: "opacity",
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.5)), url(${slides[key].img})`,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                justifyContent: "space-between",
                padding: "32px",
              }}
            >
              <Box>
                <Heading as="h2" size="lg" color="whiteAlpha.800" mb={2}>
                  Find us in
                </Heading>
                <Heading as="h3" size="2xl" color="white" mb={4}>
                  {slides[key].name}
                </Heading>
                <HStack spacing={4}>
                  {slides.map((item, i) => (
                    <Box
                      key={`cityDot${i}`}
                      w={i === key ? "32px" : "10px"}
                      h="10px"
                      borderRadius="full"
                      bg={i === key ? "white" : "whiteAlpha.500"}
                      transition="all 0.3s"
                    />
                  ))}
                </HStack>
              </Box>
              <Text
                color="whiteAlpha.800"
                fontSize="md"
                fontWeight="semibold"
                borderBottom="1px solid"
                borderColor="whiteAlpha.600"
                pb="2px"
                _hover={{ color: "white", borderColor: "white" }}
                cursor="pointer"
                alignSelf="flex-end"
                onClick={(e) => { e.stopPropagation(); trackCTA("explore_city_guides"); navigate("/cities"); }}
              >
                Explore our city guides &rarr;
              </Text>
            </animated.div>
          ))}
        </Stack>
        {/* <Stack
          justifyContent="center"
          alignItems="center"
          bg="#ebe8f1"
          padding="32px"
          fontFamily="Garamond"
          marginTop="8px"
          textAlign="center"
          height="calc(100vh - 100px)"
          spacing="4"
        >
          <Text>Are you moving to Canada in January 2025?</Text>
          <Text>Have you gotten your Canadian visa?</Text>
          <Button
            as="a"
            colorScheme="yellow"
            href="https://calendly.com/vcnng/raven-support-network"
            target="_blank"
            h="fit-content"
          >
            <Text
              fontSize="4xl"
              fontWeight="bold"
              whiteSpace="normal"
              wordBreak="break-word"
            >
              Join our January 2025 Cohort of Newcomers*
            </Text>
          </Button>
          <Text fontSize="sm" fontWeight="bold" marginTop="-8px">
            *only 12 spots available
          </Text>
        </Stack> */}
        <Flex
          justifyContent="center"
          alignItems="center"
          backgroundImage="/IMG_9459.JPG"
          backgroundSize="cover"
          backgroundPosition="center center"
          fontFamily="Garamond"
          padding="32px"
          marginTop="8px"
          height="calc(100vh - 100px)"
        >
          <Flex
            bg="rgba(239,239,239,0.75)"
            direction="column"
            justifyContent="center"
            alignItems="center"
            padding="16px"
          >
            <Text>Be Intentional About Your Immigrant Journey</Text>
            <Button
              as="a"
              colorScheme="yellow"
              marginTop="8px"
              onClick={() => {
                trackCTA("contact_us_hero");
                navigate("/contact");
              }}
              size="lg"
            >
              Contact us today
            </Button>
          </Flex>
        </Flex>
        <Flex
          direction="column"
          justifyContent="center"
          alignItems="center"
          bg="#EFEFEF"
          fontFamily="Garamond"
          padding="32px"
          marginTop="8px"
        >
          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={{ base: "8", lg: "16" }}
            alignItems="center"
          >
            <Image
              src="/friends-in-computer.jpg"
              alt="Newcomers collaborating with Raven Support Network team"
              w={{ base: "200px", lg: "250px" }}
              flex="1"
              animation="fadeIn 5s"
            />
            <Stack alignSelf="center">
              <Heading as="h2" size="xl">
                Our Commitment to You
              </Heading>
              <SimpleGrid
                spacing={8}
                fontSize="lg"
                columns={{ base: 1, sm: 2, md: 3 }}
              >
                <Card>
                  <CardBody>
                    <HStack>
                      <Icon as={FcClock} />
                      <Text fontWeight="bold">Relocation Logistics</Text>
                    </HStack>
                    <Text>We provide hands-on coordination and physical assistance to ensure your transition into Canada is stress-free</Text>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <HStack>
                      <Icon as={FcBriefcase} />
                      <Text fontWeight="bold">Career Empowerment</Text>
                    </HStack>
                    <Text>
                      Our team focuses on bridging the gap between your current skills and the requirements of the modern Canadian job market.
                    </Text>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <HStack>
                      <Icon as={FcCurrencyExchange} />
                      <Text fontWeight="bold">Financial Literacy</Text>
                    </HStack>
                    <Text>We help you build a solid foundation through personalized budgeting sessions and guidance on navigating local banking and credit systems.</Text>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <HStack>
                      <Icon as={FcConferenceCall} />
                      <Text fontWeight="bold">Social Integration</Text>
                    </HStack>
                    <Text>
                     We foster a sense of belonging by linking you with local peer groups, cultural events, and volunteer opportunities.
                    </Text>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <HStack>
                      <Icon as={FcHome} />
                      <Text fontWeight="bold">Best Accomodation</Text>
                    </HStack>
                    <Text>
                      Our team works to demystify the local rental market so you can secure the best space for your needs.
                    </Text>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <HStack>
                      <Icon as={FcTodoList} />
                      <Text fontWeight="bold">Personalized Assistance</Text>
                    </HStack>
                    <Text>
                      Whether it is navigating bureaucracy or finding specific local resources, we ensure no one walks their journey alone.                    </Text>
                  </CardBody>
                </Card>
              </SimpleGrid>
            </Stack>
          </Stack>
        </Flex>
        <Flex
          justifyContent="center"
          alignItems="center"
          bg="#ebe8f1"
          fontFamily="Garamond"
          padding="32px"
          marginTop="8px"
          direction="column"
          spacing={4}
        >
          <Heading as="h2" size="xl">
            "Share your Story"
          </Heading>
          <Text fontSize="lg" maxW="600px" textAlign="center" mt={2}>
            Listen to our podcast and hear real stories about the immigrant experience.
          </Text>
          <Button
            as="a"
            colorScheme="yellow"
            marginTop="16px"
            href="https://creators.spotify.com/pod/profile/raven-support-network/"
            target="_blank"
            size="lg"
            onClick={() => trackOutboundLink("spotify_podcast")}
          >
            Listen Now
          </Button>
        </Flex>
      </Flex>
      <Footer />
    </Flex>
  );
};

export default Information;
